import React, { useState, useEffect, useRef, useMemo } from 'react';
import style from './StepsDashboard.module.css';
import HeadSub from '../../../../../components/comm/HeadSub';
import BarChartBox from '../../../../../components/box/BarChartBox';
import { useNavigate } from 'react-router-dom';
import URL from '../../../../../constants/url';
import Slider from '@mui/material/Slider';
import { healthAPI } from '../../../../../api/healthAPI';
import Modal from '../../../../../components/comm/Modal';
import { useWebStepCounter } from '../../../../../hook/useWebStepCounter';
import { stepUpdater } from '../../../../../services/GlobalStepUpdater';
import axiosApi from '../../../../../api/daonAxios';
import CODE from '../../../../../constants/code';

const AlertBox = ({ isOpen, onClose, message, btnTxt, callback }) => {
  if (!isOpen) return null;
  const handleClicBtn = () => {
    if (callback) callback();
    onClose();
  };
  return (
    <Modal isOpen={isOpen}>
      <div className="modal-box" data-type="a">
        <div className="modal-top-box">
          <div className="title-box">
            <p style={{ whiteSpace: 'pre-line' }}>{message}</p>
          </div>
        </div>
        <div className="modal-bottom-box">
          <div className="btn-layout">
            <button className="btn" data-btn-type="fill-main" data-btn-size="md" onClick={handleClicBtn}>
              <span>{btnTxt}</span>
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const marks = [
  { value: 0, label: "3,000" },
  { value: 25, label: '5,000' },
  { value: 50, label: '10,000' },
  { value: 75, label: '20,000' },
  { value: 100, label: '30,000' },
];

const detectEnvironment = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  const isAndroidUA = /Android/i.test(userAgent);
  const isAndroidNavigator = navigator.platform === 'Linux armv7l' || navigator.platform === 'Linux aarch64';
  const isAndroidAny = isAndroidUA || isAndroidNavigator || userAgent.includes('wv');

  return {
    isReactNativeWebView: !!window.ReactNativeWebView,
    isAndroid: isAndroidAny,
    isIOS: /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream,
    isDesktopBrowser: !isAndroidAny && !(/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream),
    platform: isAndroidAny ? 'android' : (/iPad|iPhone|iPod/.test(userAgent) ? 'ios' : 'web'),
    userAgent
  };
};

const calculateSliderPosition = (steps) => {
  if (steps <= 3000) return 0;
  if (steps <= 5000) return (steps - 3000) / (5000 - 3000) * 25;
  if (steps <= 10000) return 25 + (steps - 5000) / (10000 - 5000) * 25;
  if (steps <= 20000) return 50 + (steps - 10000) / (20000 - 10000) * 25;
  if (steps <= 30000) return 75 + (steps - 20000) / (30000 - 20000) * 25;
  return 100;
};

const calculateCalories = (steps) => Math.round(steps * 0.04);
const calculateDistance = (steps) => steps === 0 ? '0' : (steps * 0.0008).toFixed(2);
const calculateRemainingSteps = (currentSteps, targetSteps = 10000) => {
  const remaining = targetSteps - currentSteps;
  return remaining > 0 ? remaining : 0;
};

const calculateYesterdayDifference = (weeklyData, currentSteps) => {
  if (!weeklyData || weeklyData.length < 2) return 0;
  const yesterdaySteps = weeklyData[weeklyData.length - 2]?.step || 0;
  return currentSteps - yesterdaySteps;
};

const calculateRealAverage = (weeklyData) => {
  if (!weeklyData || weeklyData.length === 0) return 0;
  const activeDays = weeklyData.filter(day => day.step > 0);
  if (activeDays.length === 0) return 0;
  const total = activeDays.reduce((sum, day) => sum + day.step, 0);
  return Math.round(total / activeDays.length);
};

const getTodayString = () => {
  const today = new Date();
  return today.getFullYear() +
    String(today.getMonth() + 1).padStart(2, '0') +
    String(today.getDate()).padStart(2, '0');
};

const getCurrentDateString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const dayNames = ['일', '월', '화', '수', '목', '금', '토'];
  return `${year}.${month}.${day}(${dayNames[now.getDay()]})`;
};

const convertToYYYYMMDD = (mdString) => {
  if (!mdString) return getTodayString();
  if (mdString.length === 8 && !mdString.includes('.')) return mdString;
  const today = new Date();
  const year = today.getFullYear();
  const parts = mdString.split('.');
  if (parts.length === 2) {
    const month = parts[0].padStart(2, '0');
    const day = parts[1].padStart(2, '0');
    return `${year}${month}${day}`;
  }
  return getTodayString();
};

const generateCurrentWeekData = () => {
  const data = [];
  const today = new Date();
  for (let i = 6; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(today.getDate() - i);
    const m = date.getMonth() + 1;
    const d = date.getDate();
    const dateStr = `${m}.${d}`;
    const fullDate = `${date.getFullYear()}${String(m).padStart(2, '0')}${String(d).padStart(2, '0')}`;

    data.push({
      date: dateStr,
      step: 0,
      day: i === 0 ? '오늘' : undefined,
      fullDate: fullDate
    });
  }
  return data;
};

const mergeWeeklyData = (currentData, newData) => {
  if (!currentData || currentData.length === 0) return newData;
  if (!newData || newData.length === 0) return currentData;
  return currentData.map((currentItem, index) => {
    const newItem = newData.find(d => d.date === currentItem.date) || newData[index];
    if (newItem) {
      return {
        ...currentItem,
        // step: Math.max(currentItem.step, newItem.step),
        step: Math.round(Math.max(currentItem.step, newItem.step)),
        fullDate: newItem.fullDate || currentItem.fullDate,
        day: currentItem.day || newItem.day
      };
    }
    return currentItem;
  });
};

const BrowserNoticeModal = ({ isOpen, onClose, environment }) => {
  if (!isOpen) return null;
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
      <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '10px', maxWidth: '400px', textAlign: 'center' }}>
        <p>{environment.isDesktopBrowser ? "모바일 앱에서 이용해주세요." : "앱을 다운로드해주세요."}</p>
        <button onClick={onClose}>확인</button>
      </div>
    </div>
  );
};

const BrowserFallbackBar = ({ environment, onInfoClick }) => (
  <div style={{ padding: '10px', backgroundColor: '#f0f0f0' }}>
    <span>{environment.isDesktopBrowser ? 'PC 브라우저' : '모바일 웹'}</span>
    <button onClick={onInfoClick}>자세히</button>
  </div>
);

const StepsDashboard = () => {

  const isInitializing = useRef(false);


  const navigate = useNavigate();
  const lastSavedSteps = useRef(0);
  const [environment, setEnvironment] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [imgKey, setImgKey] = useState(0);

  const [currentSteps, setCurrentSteps] = useState(() => {
    const saved = localStorage.getItem('lastKnownSteps_iOS');
    return saved ? parseInt(saved, 10) : 0;
  });

  const [chartReady, setChartReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [healthPermissionGranted, setHealthPermissionGranted] = useState(false);
  const [weeklyData, setWeeklyData] = useState(generateCurrentWeekData());
  const [weeklyAverage, setWeeklyAverage] = useState(0);
  const [yesterdayDiff, setYesterdayDiff] = useState(0);

  const [initializationComplete, setInitializationComplete] = useState(false);

  const [serverInitSteps, setServerInitSteps] = useState(() => {
    const saved = localStorage.getItem('lastKnownSteps_Total');
    const savedDate = localStorage.getItem('lastKnownSteps_Date');
    const today = getTodayString();
    return (saved && savedDate === today) ? parseInt(saved, 10) : 0;
  });

  const [isResetting, setIsResetting] = useState(false);
  const [alertState, setAlertState] = useState({ isOpen: false, message: '', btnTxt: '확인', callback: null });
  const [weeklyApiData, setWeeklyApiData] = useState([]);

  const androidStepCounter = useWebStepCounter();
  const hasPermission = (environment.isAndroid && androidStepCounter?.hasPermission) ?? false;
  const permissionChecked = androidStepCounter?.permissionChecked ?? false;
  const permissionDenied = androidStepCounter?.permissionDenied ?? false;
  const androidSteps = (environment.isAndroid && androidStepCounter?.currentSteps) ?? 0;

  const finalSteps = useMemo(() => {
    if (isResetting) return 0;
    let maxSteps = serverInitSteps || 0;
    if (environment.isAndroid && hasPermission) {
      maxSteps = Math.max(maxSteps, androidSteps || 0);
    } else if (environment.isIOS && healthPermissionGranted) {
      maxSteps = Math.max(maxSteps, currentSteps || 0);
    }
    return maxSteps;
  }, [isResetting, environment, hasPermission, healthPermissionGranted, androidSteps, currentSteps, serverInitSteps]);

  const sliderPosition = calculateSliderPosition(finalSteps);
  const calories = calculateCalories(finalSteps);
  const distance = calculateDistance(finalSteps);
  const remainingSteps = calculateRemainingSteps(finalSteps);
  const isDiffPositive = yesterdayDiff >= 0;

  const handleAlertClose = () => {
    if (alertState.callback) alertState.callback();
    setAlertState({ ...alertState, isOpen: false });
  };

  useEffect(() => {
    if (!environment.isAndroid || !hasPermission) return;

    const fetchLatestSteps = () => {
      if (androidStepCounter?.getCurrentSteps) androidStepCounter.getCurrentSteps();
    };
    fetchLatestSteps();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') fetchLatestSteps();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const handleNativeStepUpdate = (event) => {
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

        // ★ 자정 리셋 처리
        if (data.type === 'MIDNIGHT_RESET') {
          setIsResetting(true);
          setCurrentSteps(0);
          setServerInitSteps(0);
          setWeeklyData(generateCurrentWeekData());
          localStorage.removeItem('lastKnownSteps_iOS');
          localStorage.removeItem('lastKnownSteps_Total');
          localStorage.removeItem('lastKnownSteps_Date');
          lastSavedSteps.current = 0;
          setTimeout(() => setIsResetting(false), 2000);
          return;
        }

        if (data.type === 'STEP_UPDATE' && data.steps !== undefined) {
          setCurrentSteps((prev) => Math.max(prev, data.steps));
        }
      } catch (e) { }
    };
    window.addEventListener('message', handleNativeStepUpdate);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('message', handleNativeStepUpdate);
    };
  }, [environment.isAndroid, hasPermission]);

  useEffect(() => {
    if (!alertState.isOpen) setImgKey(prev => prev + 1);
  }, [alertState.isOpen]);

  useEffect(() => {
    const env = detectEnvironment();
    setEnvironment(env);
    if (env.isReactNativeWebView) {
      if (env.isIOS) {
        checkAndGuideAppInstallation();
      } else {
        setLoading(false);
      }
    } else {
      setCurrentSteps(0);
      setWeeklyData(generateCurrentWeekData());
      setLoading(false);
      const timer = setTimeout(() => setShowModal(true), 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    if (environment.isAndroid && hasPermission && permissionChecked) {
      if (androidStepCounter && typeof androidStepCounter.startTracking === 'function') {
        androidStepCounter.startTracking(); // 네이티브 백그라운드 서비스 호출
      }
    }
  }, [environment.isAndroid, hasPermission, permissionChecked, androidStepCounter]);


  // StepsDashboard.jsx — recheckPermissionOnForeground useEffect 전체 교체

  useEffect(() => {
    const getUserKey = (key) => {
      try {
        const loginUser = sessionStorage.getItem("loginUser");
        if (loginUser) {
          const user = JSON.parse(loginUser);
          return `${key}_${user.usrInfoId}`;
        }
      } catch (error) { return key; }
      return key;
    };

    const recheckPermissionOnForeground = async () => {
      if (!environment.isReactNativeWebView) return;

      // ★ 이미 권한 있으면 스킵 — 불필요한 CHECK_HEALTH_PERMISSION 호출 차단
      if (environment.isIOS && healthPermissionGranted) return;
      if (environment.isAndroid && hasPermission) return;

      const deniedKey = getUserKey(`healthPermissionDenied_${environment.isIOS ? 'ios' : 'android'}`);
      const wasPermissionDenied = localStorage.getItem(deniedKey) === 'true';

      if (environment.isIOS) {
        try {
          const permissionStatus = await healthAPI.checkPermission();
          if (permissionStatus.permissionGranted && wasPermissionDenied) {
            localStorage.removeItem(deniedKey);
            setHealthPermissionGranted(true);
          } else if (!permissionStatus.permissionGranted && !wasPermissionDenied && permissionStatus.statusDescription === '거부됨') {
            localStorage.setItem(deniedKey, 'true');
            setHealthPermissionGranted(false);
          }
        } catch (error) { }
      } else if (environment.isAndroid && permissionChecked) {
        if (hasPermission && wasPermissionDenied) {
          localStorage.removeItem(deniedKey);
        } else if (!hasPermission && !wasPermissionDenied && permissionDenied) {
          localStorage.setItem(deniedKey, 'true');
        }
      }
    };

    recheckPermissionOnForeground();
    const interval = setInterval(recheckPermissionOnForeground, 60000); // ★ 5초 → 60초
    return () => clearInterval(interval);
  }, [environment.isIOS, environment.isAndroid, environment.isReactNativeWebView, healthPermissionGranted, hasPermission, permissionChecked, permissionDenied]);

  // 차트 렌더링 지연
  useEffect(() => {
    if (weeklyData && weeklyData.length > 0) {
      const timer = setTimeout(() => setChartReady(true), 200);
      return () => clearTimeout(timer);
    }
  }, [weeklyData]);

  // 로컬 스토리지 백업
  useEffect(() => {
    if (finalSteps > 0 && !isResetting) {
      if (environment.isIOS) localStorage.setItem('lastKnownSteps_iOS', finalSteps.toString());
      localStorage.setItem('lastKnownSteps_Total', finalSteps.toString());
      localStorage.setItem('lastKnownSteps_Date', getTodayString());
    }
  }, [finalSteps, environment.isIOS, isResetting]);

  // Native 데이터 수신 및 병합
  useEffect(() => {
    if (environment.isAndroid && environment.isReactNativeWebView) {
      const handleAndroidWeeklyData = (event) => {
        const data = event.detail;
        if (data.type === 'STEP_WEEKLY_DATA' || data.type === 'STEP_WEEKLY_DATA_DB') {
          let dbData = data.weeklyData || data.data;
          if (typeof dbData === 'string') {
            try { dbData = JSON.parse(dbData); } catch (e) { }
          }
          if (dbData && Array.isArray(dbData)) {
            const processedData = dbData.map(item => {
              if (item.day === '오늘') return { ...item, day: '오늘' };
              return item;
            });
            syncHistoricalSteps(dbData);
            setWeeklyData(prev => mergeWeeklyData(prev, processedData));
          }
        }
      };
      document.addEventListener('stepData', handleAndroidWeeklyData);
      if (window.ReactNativeWebView) window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'STEP_GET_WEEKLY' }));
      return () => document.removeEventListener('stepData', handleAndroidWeeklyData);
    }
    else if (environment.isIOS && healthPermissionGranted) {
      healthAPI.getWeeklyStepCount().then(response => {
        const hkData = response?.weeklyData || response?.data?.weeklyData;
        if (hkData && Array.isArray(hkData)) {
          const processedData = hkData.map(item => {
            if (item.day === '오늘') return { ...item, day: '오늘' };
            return item;
          });
          setWeeklyData(prev => mergeWeeklyData(prev, processedData));
          syncHistoricalSteps(processedData);
        }
      }).catch(err => console.error(err));
    }
  }, [environment.isAndroid, environment.isReactNativeWebView, environment.isIOS, healthPermissionGranted]);

  // finalSteps 변경 시 즉시 반영
  useEffect(() => {
    if (finalSteps >= 0) {
      setWeeklyData(prevData => {
        return prevData.map(day => {
          if (day.day === '오늘') return { ...day, step: Math.max(day.step, finalSteps) };
          return day;
        });
      });
    }
  }, [finalSteps]);

  // 평균 계산
  useEffect(() => {
    if (weeklyData && weeklyData.length > 0) {
      setWeeklyAverage(calculateRealAverage(weeklyData));
      const todayStepVal = weeklyData.find(d => d.day === '오늘')?.step || 0;
      setYesterdayDiff(calculateYesterdayDifference(weeklyData, todayStepVal));
    }
  }, [weeklyData]);

  // 자동 저장
  useEffect(() => {
    if (finalSteps >= 0 && !loading && !isResetting) {
      const stepDifference = Math.abs(finalSteps - lastSavedSteps.current);
      if (stepDifference >= 5 || lastSavedSteps.current === 0) {
        const timer = setTimeout(() => {
          stepUpdater.sendToServer(finalSteps, 'AUTO_SAVE')
            .then(() => { lastSavedSteps.current = finalSteps; })
            .catch(error => console.error(error));
        }, 2000);
        return () => clearTimeout(timer);
      }
    }
  }, [finalSteps, loading, isResetting]);

  useEffect(() => {
    const initializeServerFirst = async () => {
      // ★ iOS는 checkAndGuideAppInstallation → finalizeHealthSync에서 처리
      if (environment.isIOS) return;

      if (!environment.isReactNativeWebView || initializationComplete) return;
      setLoading(true);
      try {
        const serverSteps = await Promise.race([
          stepUpdater.getTodayStepsFromServer(),
          new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 5000))
        ]);
        if (serverSteps > 0) {
          setServerInitSteps(serverSteps);
          if (environment.isAndroid && hasPermission) {
            window.ReactNativeWebView?.postMessage(JSON.stringify({
              type: 'STEP_SET_BASE_STEPS', payload: { steps: serverSteps }
            }));
          }
        }

        if (environment.isAndroid) {
          healthStepsWeek();
        }

        setInitializationComplete(true);
        setLoading(false);
      } catch (error) {
        setInitializationComplete(true);
        setLoading(false);
      }
    };

    if (!initializationComplete) {
      if (environment.isAndroid && hasPermission && permissionChecked) {
        initializeServerFirst();
      }
      // ★ iOS 조건 제거 — (environment.isIOS && healthPermissionGranted) 삭제
    }
  }, [environment, hasPermission, permissionChecked, initializationComplete]);

  // 자정 리셋
  useEffect(() => {
    const handleDateReset = (data) => {
      if (data.dateChanged) {
        setIsResetting(true);
        localStorage.removeItem('lastKnownSteps_iOS');
        localStorage.removeItem('lastKnownSteps_Total');
        localStorage.removeItem('lastKnownSteps_Date');

        setCurrentSteps(0);
        setServerInitSteps(0);
        setWeeklyData(generateCurrentWeekData());

        setTimeout(() => setIsResetting(false), 2000);
      }
    };
    const removeListener = stepUpdater.addListener(handleDateReset);
    return () => removeListener();
  }, []);

  const healthStepsWeek = async () => {
    const url = "/health/steps/week";
    const jToken = localStorage.getItem('jToken');
    const loginUser = sessionStorage.getItem("loginUser");
    if (!jToken || !loginUser) {
      setWeeklyData(generateCurrentWeekData());
      return;
    }
    const user = JSON.parse(loginUser);
    try {
      const res = await axiosApi.post(url, { usrInfoId: user?.usrInfoId }, {
        headers: { Authorization: `Bearer ${jToken}`, 'Content-Type': 'application/json' }
      });
      if (res.data.resultCode === CODE.RCV_SUCCESS) {
        const apiData = res.data.result.healthWalkInfo || [];
        setWeeklyApiData(apiData);
        const serverChartData = convertApiDataToChartFormat(apiData);
        setWeeklyData(prev => mergeWeeklyData(prev, serverChartData));
      }
    } catch (err) {
      console.warn("서버 데이터 로드 실패");
    }
  };

  // 일괄 데이터 서버 전송
  const syncHistoricalSteps = async (dbData) => {
    if (!dbData || !Array.isArray(dbData)) return;
    const jToken = localStorage.getItem('jToken');
    const loginUser = sessionStorage.getItem("loginUser");
    if (!jToken || !loginUser) return;

    const user = JSON.parse(loginUser);
    const walkList = dbData
      .filter(item => item.step > 0)
      .map(item => {
        const recDe = item.fullDate || convertToYYYYMMDD(item.date);
        return {
          usrInfoId: user.usrInfoId,
          recDe: recDe,
          // steps: item.step,
          steps: Math.round(item.step),
          cnsumCalor: Math.round(item.step * 0.04),
          walkDstnc: (item.step * 0.0008).toFixed(3)
        };
      });

    if (walkList.length === 0) return;
    try {
      await axiosApi.post("/health/steps/bulk", { walkList: walkList }, {
        headers: { Authorization: `Bearer ${jToken}` }
      });
    } catch (e) { }
  };

  const initializeHealthData = async () => {
    try {
      setLoading(true);

      // ★ 이미 권한 있으면 바로 sync
      if (healthPermissionGranted) {
        await finalizeHealthSync();
        return;
      }

      const appStatus = await healthAPI.checkRequiredApps().catch(() => ({ allInstalled: true }));
      if (!appStatus.allInstalled) {
        setCurrentSteps(0);
        setWeeklyData(generateCurrentWeekData());
        setLoading(false);
        return;
      }

      // ★ HEALTH_INIT 제거 — 이미 Observer가 init 처리함. 권한만 확인.
      const permissionStatus = await Promise.race([
        healthAPI.checkPermission(),
        new Promise((_, reject) => setTimeout(() => reject('timeout'), 5000))
      ]).catch(() => ({ permissionGranted: false }));

      if (!permissionStatus.permissionGranted) {
        setLoading(false);
        setAlertState({
          isOpen: true,
          message: `정확한 걸음 수 측정을 위해\n건강 데이터 접근 권한이 필요합니다.`,
          btnTxt: '권한 설정하러 가기',
          callback: async () => {
            try {
              const requestResult = await healthAPI.requestPermission();
              if (requestResult?.granted) {
                setLoading(true);
                await finalizeHealthSync();
              } else {
                setAlertState({
                  isOpen: true, message: `권한이 거부되었습니다.`, btnTxt: '확인', callback: () => navigate(-1)
                });
                setHealthPermissionGranted(false);
              }
            } catch (err) { setHealthPermissionGranted(false); }
          }
        });
        return;
      }
      await finalizeHealthSync();
    } catch (error) {
      setCurrentSteps(0);
      setHealthPermissionGranted(false);
      setLoading(false);
    }
  };

  const finalizeHealthSync = async () => {
    try {
      setHealthPermissionGranted(true);
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify({
          type: 'START_GLOBAL_OBSERVER_FROM_DASHBOARD',
          message: 'StepsDashboard에서 권한 확보 완료',
          timestamp: Date.now()
        }));
      }

      let todaySteps = 0, serverSteps = 0;

      // ★ 병렬 실행 + 짧은 timeout
      const [healthResult, serverResult] = await Promise.allSettled([
        Promise.race([
          healthAPI.getStepCount(),
          new Promise((_, reject) => setTimeout(() => reject('timeout'), 3000))
        ]),
        Promise.race([
          stepUpdater.getTodayStepsFromServer(),
          new Promise((_, reject) => setTimeout(() => reject('timeout'), 3000))
        ])
      ]);

      todaySteps = healthResult.status === 'fulfilled' ? (healthResult.value?.value || 0) : 0;
      serverSteps = serverResult.status === 'fulfilled' ? (serverResult.value || 0) : 0;

      const maxSteps = Math.max(todaySteps, serverSteps);
      setCurrentSteps(maxSteps);

      if (todaySteps > serverSteps) {
        stepUpdater.sendToServer(todaySteps, 'IOS_SYNC').catch(console.error);
      }

      setTimeout(() => healthStepsWeek(), 500);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const checkAndGuideAppInstallation = async () => {
    // ★ 이미 초기화 중이면 스킵
    if (isInitializing.current) return;

    // ★ 이미 완료 상태면 데이터 갱신만
    if (healthPermissionGranted && initializationComplete) {
      setLoading(true);
      try {
        await finalizeHealthSync();
      } catch (e) { }
      setLoading(false);
      return;
    }

    if (healthPermissionGranted) {
      setLoading(true);
      try {
        await finalizeHealthSync();
      } catch (e) { }
      setLoading(false);
      return;
    }

    isInitializing.current = true; // ★ lock
    setLoading(true);
    try {
      const result = await healthAPI.checkRequiredApps().catch(() => ({ allInstalled: true }));
      if (result.allInstalled) {
        const status = await healthAPI.checkPermission().catch(() => ({ permissionGranted: false }));
        if (!status.permissionGranted) {
          await initializeHealthData();
        } else {
          setHealthPermissionGranted(true);
          await finalizeHealthSync();
        }
      }
    } catch (e) { }
    finally {
      setLoading(false);
      isInitializing.current = false; // ★ unlock
    }
  };


  const refreshData = () => {
    if (environment.isReactNativeWebView) {
      if (environment.isIOS) initializeHealthData();
      else if (environment.isAndroid && hasPermission) window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'STEP_GET_CURRENT' }));
      if (finalSteps > 0) stepUpdater.sendToServer(finalSteps, 'REFRESH_SAVE');
    }
  };

  const convertApiDataToChartFormat = (apiData) => {
    if (!apiData || apiData.length === 0) return generateCurrentWeekData();
    const result = [];
    const today = new Date();
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateString = `${year}${month}${day}`;
      const apiItem = apiData.find(item => item.recDe === dateString);
      const isToday = i === 0;
      result.push({
        date: `${date.getMonth() + 1}.${date.getDate()}`,
        step: apiItem ? Math.max(0, Math.round(apiItem.steps || 0)) : 0,
        day: isToday ? '오늘' : undefined,
        fullDate: dateString
      });
    }
    return result;
  };

  return (
    <>
      <HeadSub title="걸음수 기록" />
      {!environment.isReactNativeWebView && <BrowserFallbackBar environment={environment} onInfoClick={() => setShowModal(true)} />}

      <div id="sub-content" data-layout="white">
        <div className={style["inner-wrap"]} data-side-pad='0'>
          <div className={style['steps-top-box']}>
            <div className={style['left-box']}>
              <span>{getCurrentDateString()}</span>
              <p>현재 걸음수</p>
              <strong onClick={refreshData}>
                {loading ? <span>로딩중...</span> : <>{finalSteps.toLocaleString()}<span>걸음</span></>}
              </strong>
            </div>
            <div className={style['img-box']}>
              <img key={imgKey} src={require('../../../../../images/user/img_3d_steps_main.png')} alt="걸음수 이미지" style={{ width: '100%', height: '100%' }} />
            </div>
          </div>

          <div className="steps-count-slider">
            <Slider min={0} step={1} max={100} value={sliderPosition} marks={marks} disabled />
          </div>

          <div className={style['steps-mid-box']}>
            <ul>
              <li className={style['remaining-steps']}>
                {finalSteps === 0 ? <p>오늘 걸음수 없음</p> : remainingSteps === 0 ? <p>🎉 목표 달성!</p> : <p><span>10,000</span> 걸음 까지 <br />남은 걸음 <span>{remainingSteps.toLocaleString()}</span></p>}
              </li>
              <li className={style['difference-steps']}>
                <p>어제보다</p>
                {yesterdayDiff === 0 ? <p className={style['neutral']}><span>동일</span></p> :
                  <p className={isDiffPositive ? style['up'] : style['down']}><span>{Math.abs(yesterdayDiff).toLocaleString()}</span> 걸음{isDiffPositive ? ' 더' : ' 적게'}</p>}
              </li>
              <li className={style['calorie-stps']}><p>소모 칼로리</p><p><b>{calories}</b> kcal</p></li>
              <li className={style['distance-steps']}><p>걸은 거리</p><p><b>{distance}</b> km</p></li>
            </ul>
          </div>

          <div className={style['steps-bot-box']}>
            <div className={style['inner-box']}>
              <p>최근 <span>7</span> 일 동안 평균 <span>{weeklyAverage.toLocaleString()}</span> 걸음을 걸었어요!</p>
              {!environment.isReactNativeWebView && <small style={{ color: '#999', fontSize: '12px', marginTop: '5px' }}>* 실제 데이터는 모바일 앱에서 확인 가능합니다</small>}
            </div>
            <div className={style['steps-graph-box']}>
              <div className={style['in-box']}>
                {chartReady ? <BarChartBox data={weeklyData} /> : <div style={{ height: '100%', minHeight: 150 }} />}
              </div>
            </div>
          </div>

          <div className="bottom-fixed-box">
            <div className="inner-box">
              <div className='btn-layout' data-btn-num="1">
                <button className="btn" data-btn-type="fill-main-sub" data-btn-size="lg" onClick={() => navigate(URL.STEPS_RECORD_LIST)}>
                  <span>전체 기록 보기</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AlertBox isOpen={alertState.isOpen} onClose={handleAlertClose} message={alertState.message} btnTxt={alertState.btnTxt} callback={alertState.callback} />
      <BrowserNoticeModal isOpen={showModal} onClose={() => setShowModal(false)} environment={environment} />
    </>
  );
};

export default StepsDashboard;